import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { 
  Bell, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Info,
  ArrowRight
} from 'lucide-react'
import { dashboardService } from '@/services/dashboardService'
import { format } from 'date-fns'

const NotificationPanel = ({ notifications = [] }) => {
  const queryClient = useQueryClient()

  const markAsReadMutation = useMutation(
    (notificationId) => dashboardService.markNotificationAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dashboard-notifications')
      }
    }
  )

  const deleteNotificationMutation = useMutation(
    (notificationId) => dashboardService.deleteNotification(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dashboard-notifications')
      }
    }
  )

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      default:
        return <Info className="h-4 w-4 text-blue-500" />
    }
  }

  const getNotificationBgColor = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200'
      case 'error':
        return 'bg-red-50 border-red-200'
      default:
        return 'bg-blue-50 border-blue-200'
    }
  }

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId)
  }

  const handleDelete = (notificationId) => {
    deleteNotificationMutation.mutate(notificationId)
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Bell className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
            {notifications.filter(n => !n.read_at).length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {notifications.filter(n => !n.read_at).length}
              </span>
            )}
          </div>
          <Link
            to="/notifications"
            className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        {notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.slice(0, 5).map((notification) => (
              <div
                key={notification.id}
                className={`p-3 rounded-lg border ${getNotificationBgColor(notification.type)} ${
                  !notification.read_at ? 'border-l-4' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${
                        !notification.read_at ? 'font-medium text-gray-900' : 'text-gray-700'
                      }`}>
                        {notification.title}
                      </p>
                      {notification.message && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {format(new Date(notification.created_at), 'MMM dd, HH:mm')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 ml-2">
                    {!notification.read_at && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Mark as read"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(notification.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete notification"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {notification.action_url && (
                  <div className="mt-2">
                    <Link
                      to={notification.action_url}
                      className="text-xs text-primary-600 hover:text-primary-500"
                    >
                      {notification.action_text || 'View details'}
                    </Link>
                  </div>
                )}
              </div>
            ))}
            
            {notifications.length > 5 && (
              <div className="text-center pt-2">
                <Link
                  to="/notifications"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  View {notifications.length - 5} more notifications
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No notifications</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationPanel